import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import {
  registrationService,
  workshopService,
  settingsService,
  giftVoucherService,
  GiftVoucher,
} from "../services/firebase";
import { emailService } from "../services/emailService";
import "./PaymentSuccess.css";
import "./VoucherPaymentSuccess.css";
import RightArrow from "assets/icone/RightArrow";
import RightArrowMoblie from "assets/icone/RightArrowMoblie";
import PrimaryButton from "components/PrimaryButton";
import { formatDateIsrael } from "../utils/dateUtils";

// Component for responsive RightArrow
const ResponsiveRightArrow: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth >= 390 && window.innerWidth <= 809);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return isMobile ? <RightArrowMoblie /> : <RightArrow />;
};

const PaymentSuccess: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const voucherIdParam =
    searchParams.get("voucherId") ||
    new URLSearchParams(window.location.search).get("voucherId");
  const isVoucherFlow = Boolean(voucherIdParam);
  const [registration, setRegistration] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [voucher, setVoucher] = useState<GiftVoucher | null>(null);
  const [isVoucherPurchase, setIsVoucherPurchase] = useState(false);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const processingRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if we're inside an iframe and redirect to top level
    if (window.self !== window.top && window.top) {
            window.top.location.href = window.location.href;
      return;
    }

    // Show the result screen immediately based on the URL params returned by
    // Tranzila, and clear the spinner right away. The Firestore reconciliation
    // below is best-effort and must NEVER block the UI: Safari can stall
    // Firestore reads/writes after the cross-origin Tranzila iframe redirect.
    // The server-to-server notify (paymentCallback) is the source of truth for
    // recording the payment and sending the confirmation email, so even if the
    // work below is blocked, the customer still sees the correct result and the
    // payment is recorded server-side.
    const urlTransactionId =
      searchParams.get("transaction_id") ||
      searchParams.get("TranzilaTranzilaMP");
    if (urlTransactionId) {
      setTransactionId(urlTransactionId);
    }
    if (voucherIdParam) {
      setIsVoucherPurchase(true);
    } else if (
      searchParams.get("registrationId") ||
      new URLSearchParams(window.location.search).get("registrationId")
    ) {
      setPaymentData({
        transactionId: urlTransactionId,
        amount: searchParams.get("sum") || searchParams.get("TranzilaSum"),
        currency: searchParams.get("currency") || "ILS",
        status: "success",
      });
    }
    setLoading(false);

    const loadPaymentData = async () => {
      try {
        // Gift voucher purchase return
        if (voucherIdParam) {
          setIsVoucherPurchase(true);

          const txnId =
            searchParams.get("transaction_id") ||
            searchParams.get("TranzilaTranzilaMP") ||
            undefined;

          const activated = await giftVoucherService.activate(
            voucherIdParam,
            txnId || undefined
          );
          setVoucher(activated);
          setVoucherError(null);

          // Send the confirmation email in the background. It is non-critical
          // and must never block showing the success screen (the EmailJS / extra
          // Firestore reads can be slow, especially in Safari after the iframe
          // redirect). Fire-and-forget so the spinner clears as soon as the
          // voucher is activated.
          void (async () => {
            try {
              // Both this client path and the server-side paymentCallback can
              // send the voucher email. Claim the send atomically via the shared
              // confirmationEmailSent flag so exactly one of them sends it and
              // the customer never gets a duplicate.
              const shouldSend =
                await giftVoucherService.claimConfirmationEmail(voucherIdParam);
              if (!shouldSend) {
                return;
              }

              const lang = activated.language === "ru" ? "ru" : "he";
              const fixedT = i18n.getFixedT(lang);
              const siteSettings = await settingsService.get();
              await emailService.sendGiftVoucherEmail(
                {
                  customer_email: activated.purchaserEmail,
                  purchaser_name: activated.purchaserName,
                  code: activated.code,
                  participants: activated.participants,
                  amount: activated.amountPaid,
                  site_name: siteSettings.siteName || "האתר",
                },
                {
                  subject: fixedT("emails.giftVoucher.subject"),
                  title: fixedT("emails.giftVoucher.title"),
                  greeting: fixedT("emails.giftVoucher.greeting"),
                  body: fixedT("emails.giftVoucher.body"),
                  codeLabel: fixedT("emails.giftVoucher.codeLabel"),
                  participantsLabel: fixedT(
                    "emails.giftVoucher.participantsLabel"
                  ),
                  howToUse: fixedT("emails.giftVoucher.howToUse"),
                  regards: fixedT("emails.giftVoucher.regards"),
                  teamLabel: fixedT("emails.giftVoucher.teamLabel"),
                  footer: fixedT("emails.giftVoucher.footer"),
                  defaultName: fixedT("emails.giftVoucher.defaultName"),
                },
                lang
              );
            } catch (emailError) {
                        }
          })();

          return;
        }

        // Workshop registration return
        const registrationId =
          searchParams.get("registrationId") ||
          new URLSearchParams(window.location.search).get("registrationId");

        // Tranzila מפנה ישירות לדף הזה, אז אנחנו מניחים שהתשלום הצליח
        // אם יש registrationId, זה אומר שהתשלום הצליח
        const isPaymentSuccess = !!registrationId;

        
        if (registrationId) {
          const data = await registrationService.getById(registrationId);
          setRegistration(data);

          let workshopData = null;
          // טעינת פרטי הסדנה
          if (data && data.workshopId) {
            try {
              workshopData = await workshopService.getById(data.workshopId);
            } catch (error) {
                          }
          }

          let siteSettings = null;
          // טעינת הגדרות האתר
          try {
            siteSettings = await settingsService.get();
          } catch (error) {
                      }

          // קבלת מספר עסקה מ-Tranzila או מה-registration הקיים
          const resolvedTransactionId =
            searchParams.get("transaction_id") ||
            searchParams.get("TranzilaTranzilaMP") ||
            data.transactionId;

          // שמירת מספר העסקה ב-state כדי שיהיה זמין לתצוגה
          setTransactionId(resolvedTransactionId || null);

          
          // עדכון סטטוס התשלום - Tranzila מפנה לכאן רק אם התשלום הצליח
          await registrationService.updatePaymentStatus(
            registrationId,
            "paid",
            resolvedTransactionId
          );

          // עדכון סטטוס ההרשמה לאושר אחרי תשלום מוצלח
          await registrationService.updateStatus(registrationId, "confirmed");

          // עדכון מספר המקומות הפנויים אחרי תשלום מוצלח (פעם אחת בלבד)
          try {
            await registrationService.confirmRegistrationSpots(registrationId);
                      } catch (error) {
                      }

          // מימוש שובר מתנה אם שימש בהרשמה (חד-פעמי, מוגן בטרנזקציה)
          if (data && data.voucherCode) {
            try {
              await giftVoucherService.redeem(data.voucherCode, registrationId);
                          } catch (error) {
                          }
          }

          // שמירת נתוני התשלום - השתמש במספר העסקה שנמצא
          setPaymentData({
            transactionId: resolvedTransactionId,
            amount: searchParams.get("sum") || searchParams.get("TranzilaSum"),
            currency: searchParams.get("currency") || "ILS",
            status: "success",
          });

          // שליחת מייל אישור תשלום (פעם אחת בלבד, מוגן ב-Firestore)
          if (workshopData && siteSettings && resolvedTransactionId) {
            try {
              const workshopDate = workshopData.dates?.find(
                (date: { id: string; time?: string }) =>
                  date.id === data.workshopDateId
              );

              const emailSent =
                await registrationService.sendConfirmationEmailIfNeeded(
                  registrationId,
                  {
                    customer_email: data.email,
                    customer_name: data.name,
                    workshop_title: workshopData.title,
                    workshop_date: formatDateIsrael(data.workshopDate),
                    workshop_time:
                      data.workshopTime || workshopDate?.time || "",
                    tickets_count: data.ticketsCount || 1,
                    total_amount: data.totalAmount,
                    transaction_id: resolvedTransactionId,
                    site_name: siteSettings.siteName || "האתר",
                  }
                );

              if (emailSent) {
                              }
            } catch (error) {
                          }
          }
        }
      } catch (error) {
                if (isVoucherFlow) {
          setVoucherError(
            error instanceof Error ? error.message : t("giftVoucherActivationError")
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (processingRef.current) {
      return;
    }

    processingRef.current = loadPaymentData().finally(() => {
      processingRef.current = null;
    });
  }, [searchParams, i18n, voucherIdParam, isVoucherFlow, t]);

  const displayAmount =
    typeof registration?.totalAmount === "number"
      ? registration.totalAmount
      : (() => {
          const paymentAmount = paymentData?.amount;
          if (
            paymentAmount !== undefined &&
            paymentAmount !== null &&
            paymentAmount !== ""
          ) {
            const parsed = Number(paymentAmount);
            return Number.isNaN(parsed) ? 0 : parsed;
          }
          return 0;
        })();

  const displayTransactionId = (() => {
    const raw =
      transactionId ||
      registration?.transactionId ||
      registration?.orderId ||
      paymentData?.transactionId;
    if (!raw || String(raw).startsWith("FREE_")) {
      return null;
    }
    return String(raw);
  })();

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">
          <FaCheckCircle className="fa-spin" size={40} />
          <p>{t("processingPayment")}</p>
        </div>
      </div>
    );
  }

  if (isVoucherFlow || isVoucherPurchase) {
    return (
      <div className="voucher-success-container">
        <Link className="voucher-success-back" to="/">
          <div className="voucher-success-back-icon">
            <ResponsiveRightArrow />
          </div>
          {t("backToHome")}
        </Link>

        <div className="voucher-success-header">
          <span className="voucher-success-emoji" aria-hidden="true">
            🎁
          </span>
          <h1 className="voucher-success-title">
            {t("giftVoucherSuccessTitle")}
          </h1>
          <p className="voucher-success-message">
            {t("giftVoucherSuccessMessage")}
          </p>
        </div>

        {voucherError && (
          <p className="voucher-success-error" role="alert">
            {voucherError}
          </p>
        )}

        {voucher && voucher.code && (
          <div className="voucher-success-card">
            <div className="voucher-success-code-label">
              {t("giftVoucherYourCode")}
            </div>
            <div className="voucher-success-code">{voucher.code}</div>
            <p className="voucher-success-code-note">
              {t("giftVoucherCodeNote")}
            </p>
            <PrimaryButton fullWidth={true} onClick={() => navigate("/")}>
              {t("backToHome")}
            </PrimaryButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      <Link className="back-button-payment-success" to="/">
        <div className="back-button-payment-success-icon">
          <ResponsiveRightArrow />
        </div>
        {t("backToHome")}
      </Link>
      <div className="page-success-header">
        <h1 className="page-success-title">{t("paymentSuccess")}</h1>
        <p className="page-success-message">{t("paymentSuccessMessage")}</p>
      </div>

      {(paymentData || registration) && (
        <div className="page-payment-details">
          <h3>{t("paymentDetails")}</h3>
          <div className="page-payment-info">
            {displayTransactionId && (
              <div className="page-payment-item">
                <span className="page-payment-item-text-bold">
                  {t("transactionId")} : {""}
                </span>
                <span className="page-payment-item-text">
                  {displayTransactionId}
                </span>
              </div>
            )}
            <div className="page-payment-item">
              <span className="page-payment-item-text-bold">
                {t("amount")} :
              </span>
              <span className="page-payment-item-text">
                ₪{Number(displayAmount.toFixed(2))}
              </span>
            </div>
            <div className="page-payment-item">
              <span className="page-payment-item-text-bold">
                {t("status")} :{""}
              </span>
              <span className="page-payment-item-text">{t("approved")}</span>
            </div>
            <PrimaryButton
              onClick={() => {
                navigate("/");
              }}
              fullWidth={true}
            >
              {t("backToHome")}
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* {registration && (
        <div className="page-registration-summary">
          <h3>פרטי ההרשמה</h3>
          <div className="page-summary-details">
            <div className="page-summary-item">
              <FaCalendarAlt />
              <span>תאריך: {formatDate(registration.workshopDate)}</span>
            </div>
            {registration.workshopTime && (
              <div className="page-summary-item">
                <FaClock />
                <span>שעה: {registration.workshopTime}</span>
              </div>
            )}
            {registration.ticketsCount && registration.ticketsCount > 1 && (
              <div className="page-summary-item">
                <FaUsers />
                <span>מספר כרטיסים: {registration.ticketsCount}</span>
              </div>
            )}
            <div className="page-summary-item total">
              <span>סה"כ לתשלום: ₪{registration.totalAmount}</span>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default PaymentSuccess;
